import React, { useEffect, useMemo, useState } from "react";
import { ArrowLeft, Search, SlidersHorizontal } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useAuth } from "../../../../contexts/AuthContext";
import { getAccessToken } from "../../../../awsAuth";
import "./VacanciesAll.scss";
import { photoVariant } from "../../../../utils/imageUrl";

const API_BASE =
  process.env.REACT_APP_API_URL ||
  "https://cvanto-api.onrender.com";

export default function VacanciesAll() {
  const navigate = useNavigate();
  const params = useParams();
  const { userId } = useAuth();

  const viewedUserId = params.userId || userId;
  const isOwn = !!(userId && String(viewedUserId) === String(userId));

  const [vacancies, setVacancies] = useState([]);
  const [businessName, setBusinessName] = useState("Вакансії");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("newest");
  const [format, setFormat] = useState("all");

  const [busyId, setBusyId] = useState(null);
  const [confirmDeleteItem, setConfirmDeleteItem] = useState(null);
  const [actionError, setActionError] = useState("");

  useEffect(() => {
    let alive = true;

    async function loadVacancies() {
      if (!viewedUserId) {
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError("");

        const isOwn = userId && String(viewedUserId) === String(userId);

        const token = await getAccessToken().catch(() => null);
        const headers = token ? { Authorization: `Bearer ${token}` } : {};

        const vacanciesUrl = isOwn
          ? `${API_BASE}/api/business/vacancies/me`
          : `${API_BASE}/api/business/vacancies/user/${encodeURIComponent(
              viewedUserId
            )}`;

        const vacanciesRes = await fetch(vacanciesUrl, { headers });
        const vacanciesData = await vacanciesRes.json().catch(() => ({}));

        if (!vacanciesRes.ok) {
          throw new Error(
            vacanciesData?.error || "Не вдалося завантажити вакансії"
          );
        }

        const profileRes = await fetch(
          `${API_BASE}/api/business/profile/${encodeURIComponent(
            viewedUserId
          )}`
        );

        const profileData = await profileRes.json().catch(() => ({}));

        if (!alive) return;

        setVacancies(
          Array.isArray(vacanciesData?.items) ? vacanciesData.items : []
        );

        setBusinessName(
          profileData?.userBase?.username ||
            profileData?.profile?.title ||
            profileData?.profile?.hero?.title ||
            "Вакансії"
        );
      } catch (err) {
        console.error(err);
        if (alive) setError(err.message || "Помилка завантаження");
      } finally {
        if (alive) setLoading(false);
      }
    }

    loadVacancies();

    return () => {
      alive = false;
    };
  }, [viewedUserId, userId]);

  const filteredVacancies = useMemo(() => {
    let next = Array.isArray(vacancies) ? [...vacancies] : [];

    const q = query.trim().toLowerCase();

    if (q) {
      next = next.filter((item) => {
        const text = `${item.title || ""} ${item.description || ""} ${
          item.location || ""
        } ${item.workFormat || ""} ${item.employmentType || ""}`.toLowerCase();

        return text.includes(q);
      });
    }

    if (format !== "all") {
      next = next.filter((item) => item.workFormat === format);
    }

    if (sort === "salaryAsc") {
      next.sort(
        (a, b) =>
          Number(a.salaryFrom || a.price || 0) -
          Number(b.salaryFrom || b.price || 0)
      );
    }

    if (sort === "salaryDesc") {
      next.sort(
        (a, b) =>
          Number(b.salaryFrom || b.price || 0) -
          Number(a.salaryFrom || a.price || 0)
      );
    }

    if (sort === "oldest") {
      next.sort((a, b) => Number(a.createdAt || 0) - Number(b.createdAt || 0));
    }

    if (sort === "newest") {
      next.sort((a, b) => Number(b.createdAt || 0) - Number(a.createdAt || 0));
    }

    return next;
  }, [vacancies, query, sort, format]);

  const formatSalary = (item) => {
    const from = Number(item.salaryFrom || item.price || 0);
    const to = Number(item.salaryTo || 0);
    const currency = item.currency || "ГРН";

    if (from > 0 && to > 0) return `${from}–${to} ${currency}`;
    if (from > 0) return `від ${from} ${currency}`;

    return "За домовленістю";
  };

  const formatMeta = (item) => {
    const parts = [
      item.workFormat,
      item.employmentType,
      item.experience,
      item.location,
    ].filter(Boolean);

    return parts.length ? parts.join(" · ") : "Деталі не вказані";
  };

  const authHeaders = async () => {
    const token = await getAccessToken().catch(() => null);
    return token ? { Authorization: `Bearer ${token}` } : {};
  };

  const toggleHidden = async (item) => {
    if (busyId) return;

    const id = item.listingId || item.id;
    const hiding = (item.status || "active") !== "hidden";
    const action = hiding ? "hide" : "unhide";

    setBusyId(id);
    setActionError("");

    try {
      const headers = await authHeaders();
      const res = await fetch(
        `${API_BASE}/api/business/vacancies/${encodeURIComponent(id)}/${action}`,
        { method: "PATCH", headers }
      );
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || "Не вдалося змінити видимість");
      }

      setVacancies((prev) =>
        prev.map((v) =>
          (v.listingId || v.id) === id
            ? { ...v, status: hiding ? "hidden" : "active" }
            : v
        )
      );
    } catch (err) {
      setActionError(err.message || "Не вдалося змінити видимість");
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    const item = confirmDeleteItem;
    if (!item) return;

    const id = item.listingId || item.id;

    setBusyId(id);
    setActionError("");

    try {
      const headers = await authHeaders();
      const res = await fetch(
        `${API_BASE}/api/business/vacancies/${encodeURIComponent(id)}`,
        { method: "DELETE", headers }
      );
      const data = await res.json().catch(() => ({}));

      if (!res.ok) {
        throw new Error(data?.error || "Не вдалося видалити вакансію");
      }

      setVacancies((prev) => prev.filter((v) => (v.listingId || v.id) !== id));
    } catch (err) {
      setActionError(err.message || "Не вдалося видалити вакансію");
    } finally {
      setBusyId(null);
      setConfirmDeleteItem(null);
    }
  };

  if (loading) {
    return (
      <main className="vcAll">
        <div className="vcAll__state">Завантаження вакансій...</div>
      </main>
    );
  }

  if (error) {
    return (
      <main className="vcAll">
        <div className="vcAll__state vcAll__state--error">{error}</div>
      </main>
    );
  }

  return (
    <main className="vcAll">
      <header className="vcAll__hero">
        <button type="button" className="vcAll__back" onClick={() => navigate(-1)}>
          <ArrowLeft size={22} strokeWidth={2.5} />
        </button>

        <div>
          <span>Бізнес-вакансії</span>
          <h1>{businessName}</h1>
          <p>{vacancies.length} вакансій</p>
        </div>
      </header>

      <section className="vcAll__filters">
        <label className="vcAll__search">
          <Search size={18} strokeWidth={2.5} />
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Пошук вакансій"
          />
        </label>

        <div className="vcAll__selects">
          <label>
            <SlidersHorizontal size={16} strokeWidth={2.5} />
            <select value={sort} onChange={(e) => setSort(e.target.value)}>
              <option value="newest">Нові спочатку</option>
              <option value="oldest">Старі спочатку</option>
              <option value="salaryAsc">Менша зарплата</option>
              <option value="salaryDesc">Більша зарплата</option>
            </select>
          </label>

          <label>
            <select value={format} onChange={(e) => setFormat(e.target.value)}>
              <option value="all">Усі формати</option>
              <option value="Офіс">Офіс</option>
              <option value="Віддалено">Віддалено</option>
              <option value="Гібрид">Гібрид</option>
            </select>
          </label>
        </div>
      </section>

      {actionError && <div className="vcAll__actionError">{actionError}</div>}

      {filteredVacancies.length === 0 ? (
        <section className="vcAll__empty">
          <strong>Вакансій не знайдено</strong>
          <p>Спробуй змінити пошук або фільтри.</p>
        </section>
      ) : (
        <section className="vcAll__grid">
          {filteredVacancies.map((item) => {
            const photo = item.mainPhoto || item.photos?.[0] || "";
            const id = item.listingId || item.id;
            const hidden = (item.status || "active") === "hidden";
            const isBusy = busyId === id;

            return (
              <article
                key={id}
                className={`vcAllCard${hidden ? " vcAllCard--hidden" : ""}`}
                onClick={() => navigate(`/listing/${id}`)}
              >
                <div className="vcAllCard__image">
                  {photo ? (
                    <img src={photoVariant(photo, "sm")} alt="" loading="lazy" decoding="async" />
                  ) : (
                    <span>💼</span>
                  )}

                  <em>Вакансія</em>
                  {hidden && <span className="vcAllCard__hiddenBadge">Приховано</span>}
                </div>

                <div className="vcAllCard__body">
                  <strong>{item.title || "Вакансія"}</strong>
                  <em>{formatMeta(item)}</em>

                  <div>
                    <b>{formatSalary(item)}</b>
                    <button type="button">Відкрити</button>
                  </div>

                  {isOwn && (
                    <div
                      className="vcAllCard__actions"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <button
                        type="button"
                        disabled={isBusy}
                        onClick={() => toggleHidden(item)}
                      >
                        {hidden ? "Показати" : "Приховати"}
                      </button>
                      <button
                        type="button"
                        className="vcAllCard__delete"
                        disabled={isBusy}
                        onClick={() => setConfirmDeleteItem(item)}
                      >
                        Видалити
                      </button>
                    </div>
                  )}
                </div>
              </article>
            );
          })}
        </section>
      )}

      {confirmDeleteItem && (
        <div className="vcAll__confirmOverlay" onMouseDown={() => setConfirmDeleteItem(null)}>
          <div className="vcAll__confirmModal" onMouseDown={(e) => e.stopPropagation()}>
            <p>
              Ви впевнені, що хочете видалити цей блок? Дані буде видалено без можливості
              відновлення.
            </p>
            <div className="vcAll__confirmActions">
              <button type="button" onClick={() => setConfirmDeleteItem(null)}>
                Скасувати
              </button>
              <button
                type="button"
                className="vcAll__confirmDelete"
                disabled={busyId === (confirmDeleteItem.listingId || confirmDeleteItem.id)}
                onClick={confirmDelete}
              >
                Видалити
              </button>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}